import {Component, OnDestroy, OnInit, inject} from '@angular/core';
import {DatePipe} from '@angular/common';
import {ActivatedRoute, Router} from '@angular/router';
import {HttpErrorResponse} from '@angular/common/http';
import {MatDialog} from '@angular/material/dialog';
import {MatMenuModule} from '@angular/material/menu';
import {MatSlideToggleModule} from '@angular/material/slide-toggle';
import {MatSnackBar} from '@angular/material/snack-bar';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {MatTooltipModule} from '@angular/material/tooltip';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {TablerIconsModule} from 'angular-tabler-icons';
import {Observable, Subscription, concatMap, from, interval} from 'rxjs';
import {apiErrorMessage} from '@services/api/admin/admin-api.service';
import {DocItem, DocumentsAdminApiService, RagStatus} from '@services/api/admin/documents-admin-api.service';
import {ModalsConfirmComponent} from '@components/modals/modals-confirm/modals-confirm.component';
import {TextPromptDialogComponent, TextPromptData} from '../dialogs/text-prompt-dialog.component';
import {MoveDialogComponent} from '../dialogs/move-dialog.component';

const MAX_PDF_BYTES = 50 * 1024 * 1024;
const POLL_MS = 4000;
const BUSY: RagStatus[] = ['pending', 'deleting'];

@Component({
  selector: 'app-admin-documents',
  standalone: true,
  imports: [DatePipe, MatMenuModule, MatSlideToggleModule, MatProgressSpinnerModule, MatTooltipModule, TranslateModule, TablerIconsModule],
  templateUrl: './admin-documents.component.html',
  styleUrls: ['../admin-page.scss', './admin-documents.component.scss'],
})
export class AdminDocumentsComponent implements OnInit, OnDestroy {
  private api = inject(DocumentsAdminApiService);
  private dialog = inject(MatDialog);
  private snackBar = inject(MatSnackBar);
  private translate = inject(TranslateService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  /** Percorso dalla cartella principale a quella aperta */
  path: DocItem[] = [];
  items: DocItem[] = [];
  loading = true;
  error = '';
  uploading = 0;
  dragOver = false;
  private poll?: Subscription;

  ngOnInit(): void {
    // La cartella aperta sta nell'URL (?folder=): sopravvive al ricaricamento della pagina
    const folderId = Number(this.route.snapshot.queryParamMap.get('folder'));
    if (!folderId) {
      this.load();
      return;
    }
    this.api.folders().subscribe({
      next: (folders) => {
        const byId = new Map(folders.map((f) => [f.id, f]));
        const path: DocItem[] = [];
        for (let f = byId.get(folderId); f && path.length < 50; f = f.parent ? byId.get(f.parent) : undefined) {
          path.unshift(f);
        }
        this.path = path;
        this.load();
      },
      error: () => this.load(),
    });
  }

  ngOnDestroy(): void {
    this.poll?.unsubscribe();
  }

  get currentFolder(): DocItem | null {
    return this.path.length ? this.path[this.path.length - 1] : null;
  }

  get folders(): DocItem[] {
    return this.items.filter((i) => i.type === 'folder');
  }

  get files(): DocItem[] {
    return this.items.filter((i) => i.type === 'file');
  }

  load(showSpinner = true): void {
    this.loading = showSpinner && !this.items.length;
    this.api.list(this.currentFolder?.id ?? null).subscribe({
      next: (items) => {
        this.items = items;
        this.loading = false;
        this.error = '';
        this.updatePolling();
      },
      error: (err) => {
        this.loading = false;
        this.error = apiErrorMessage(err, this.translate.instant('admin.error_load'));
      },
    });
  }

  // --- Navigazione ---

  openFolder(folder: DocItem): void {
    this.navigate([...this.path, folder]);
  }

  goTo(depth: number): void {
    this.navigate(this.path.slice(0, depth));
  }

  private navigate(path: DocItem[]): void {
    this.path = path;
    this.items = [];
    this.router.navigate([], {queryParams: {folder: this.currentFolder?.id ?? null}, replaceUrl: true});
    this.load();
  }

  // --- Upload ---

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.upload(Array.from(input.files ?? []));
    input.value = '';
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.dragOver = false;
    this.upload(Array.from(event.dataTransfer?.files ?? []));
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.dragOver = true;
  }

  private upload(files: File[]): void {
    const pdfs = files.filter((f) => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf'));
    if (pdfs.length < files.length) {
      this.toast(this.translate.instant('documents.error_not_pdf'));
    }
    const valid = pdfs.filter((f) => f.size <= MAX_PDF_BYTES);
    if (valid.length < pdfs.length) {
      this.toast(this.translate.instant('documents.error_too_big'));
    }
    if (!valid.length) {
      return;
    }
    const parent = this.currentFolder?.id ?? null;
    this.uploading += valid.length;
    // In sequenza: ogni PDF viaggia in base64 nel body
    from(valid).pipe(concatMap((file) => this.uploadOne(file, parent))).subscribe({
      complete: () => this.load(false),
    });
  }

  private uploadOne(file: File, parent: number | null): Observable<void> {
    return new Observable<void>((subscriber) => {
      const finish = () => {
        this.uploading = Math.max(0, this.uploading - 1);
        subscriber.next();
        subscriber.complete();
      };
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = String(reader.result).replace(/^data:[^;]*;base64,/, 'data:application/pdf;base64,');
        this.api.uploadPdf(file.name.replace(/\.pdf$/i, ''), parent, base64).subscribe({
          // Disponibile all'AI per default: l'indicizzazione parte subito
          next: (doc) => this.api.enableAi(doc.uuid).subscribe({complete: finish, error: finish}),
          error: (err) => {
            this.toast(apiErrorMessage(err, this.translate.instant('documents.error_upload')));
            finish();
          },
        });
      };
      reader.onerror = finish;
      reader.readAsDataURL(file);
    });
  }

  // --- Cartelle e documenti ---

  newFolder(): void {
    this.prompt({title: 'admin.docs_new_folder', label: 'admin.docs_folder_name', confirm: 'admin.docs_create'})
      .subscribe((name) => name && this.run(this.api.createFolder(name, this.currentFolder?.id ?? null), 'admin.docs_folder_created'));
  }

  rename(item: DocItem): void {
    this.prompt({title: 'admin.docs_rename', label: 'admin.docs_new_name', confirm: 'admin.save', value: item.name})
      .subscribe((name) => name && name !== item.name && this.run(this.api.update(item.id, {name}), 'admin.user_saved'));
  }

  move(item: DocItem): void {
    this.api.folders().subscribe((folders) => {
      this.dialog.open(MoveDialogComponent, {data: {item, folders}, width: '480px', maxWidth: '94vw', panelClass: 'dm-admin-dialog'})
        .afterClosed().subscribe((target?: number) => {
          if (target !== undefined) {
            this.run(this.api.update(item.id, {parent: target || null}), 'admin.docs_moved');
          }
        });
    });
  }

  remove(item: DocItem): void {
    const usedByAi = item.type === 'file' && ['analyzed', 'pending', 'error'].includes(item.rag_status);
    const message = item.type === 'folder'
      ? 'admin.docs_delete_folder_message'
      : usedByAi ? 'admin.docs_delete_ai_message' : 'admin.docs_delete_message';
    this.dialog.open(ModalsConfirmComponent, {
      panelClass: 'dm-admin-dialog',
      data: {
        title: item.type === 'folder' ? 'admin.docs_delete_folder_title' : 'admin.docs_delete_title',
        message: this.translate.instant(message, {name: item.name}),
        confirmText: 'admin.docs_delete',
        cancelText: 'admin.cancel',
      },
    }).afterClosed().subscribe((confirmed) => confirmed && this.run(this.api.delete(item.id), 'admin.docs_deleted'));
  }

  /** Interruttore "Disponibile all'AI" */
  toggleAi(item: DocItem, enabled: boolean): void {
    item.rag_status = enabled ? 'pending' : 'deleting';
    this.run(enabled ? this.api.enableAi(item.uuid) : this.api.disableAi(item.uuid),
      enabled ? 'admin.docs_ai_enabled' : 'admin.docs_ai_disabled');
  }

  retry(item: DocItem): void {
    this.run(this.api.enableAi(item.uuid), 'admin.docs_ai_enabled');
  }

  isAiOn(item: DocItem): boolean {
    return item.rag_status === 'analyzed' || item.rag_status === 'pending';
  }

  isBusy(item: DocItem): boolean {
    return BUSY.includes(item.rag_status);
  }

  openPdf(item: DocItem): void {
    // La scheda si apre subito, nel gesto dell'utente, o il browser la blocca come popup
    const win = window.open('', '_blank');
    this.api.pdf(item.uuid).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(new Blob([blob], {type: 'application/pdf'}));
        if (win) {
          win.location.href = url;
        } else {
          window.open(url, '_blank');
        }
      },
      error: () => {
        win?.close();
        this.toast(this.translate.instant('documents.error_open'));
      },
    });
  }

  formatSize(bytes: number): string {
    if (!bytes) {
      return '';
    }
    return bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  // --- Supporto ---

  private run(request: Observable<unknown>, successKey: string): void {
    request.subscribe({
      next: () => {
        this.toast(this.translate.instant(successKey));
        this.load(false);
      },
      error: (err: HttpErrorResponse) => {
        this.toast(apiErrorMessage(err, this.translate.instant('admin.error_generic')));
        this.load(false);
      },
    });
  }

  private prompt(data: TextPromptData): Observable<string | undefined> {
    return this.dialog.open(TextPromptDialogComponent, {data, width: '440px', maxWidth: '94vw', panelClass: 'dm-admin-dialog'})
      .afterClosed();
  }

  /** Aggiornamento automatico finche' qualche documento e' in indicizzazione o in eliminazione */
  private updatePolling(): void {
    const busy = this.items.some((i) => this.isBusy(i));
    if (busy && !this.poll) {
      this.poll = interval(POLL_MS).subscribe(() => this.load(false));
    } else if (!busy && this.poll) {
      this.poll.unsubscribe();
      this.poll = undefined;
    }
  }

  private toast(message: string): void {
    this.snackBar.open(message, this.translate.instant('admin.close'), {duration: 4000});
  }
}
